import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

export default function Signup() {
  const { signup, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState("client");
  const [leadWork, setLeadWork] = useState(false);
  const [helperWork, setHelperWork] = useState(true);
  const [level, setLevel] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (user) {
    nav("/app");
    return null;
  }

  async function submit(e) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await signup(email, password, role, displayName || null, role === "independent" ? {accepts_lead_work:leadWork, accepts_helper_work:helperWork, technician_level:level || null} : {});
      toast.success("Account created. Please verify your email.");
      nav("/app");
    } catch (e) {
      setErr(e?.problem?.detail || e?.problem?.title || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto avcp-card p-8">
        <h1 className="text-3xl">Create an account</h1>
        <p className="mt-2 text-sm text-[var(--avcp-ink-soft)]">1. Choose how you work. 2. Enter your details. 3. Verify your email and complete your profile.</p>
        <form onSubmit={submit} data-testid={TID.signupForm} className="mt-6 grid gap-4">
          <fieldset className="grid gap-2"><legend className="text-sm font-semibold mb-2">How will you use Workforce Partners?</legend>
            {[
              {id:'client',title:'Client — I am hiring',detail:'Create projects and book a company or an individual technician.',test:TID.signupRoleClient},
              {id:'independent',title:'Independent AV technician — I work on my own',detail:'One person, no crew of my own. I can work as a lead or join another crew.',test:'signup-role-independent'},
              {id:'contractor',title:'Contractor company — we provide a crew',detail:'Manage job requests and provide a team.',test:TID.signupRoleContractor},
            ].map(option=><label key={option.id} className={`flex gap-3 border rounded-xl p-4 cursor-pointer ${role===option.id?'border-teal-700 bg-teal-50':'border-[var(--avcp-line)]'}`}>
              <input type="radio" name="accountType" value={option.id} checked={role===option.id} onChange={()=>setRole(option.id)} data-testid={option.test} />
              <span><strong className="block">{option.title}</strong><span className="block text-sm text-[var(--avcp-ink-soft)] mt-1">{option.detail}</span></span>
            </label>)}
          </fieldset>
          {role==='independent'&&<fieldset className="rounded-xl bg-[var(--avcp-bg)] p-4 grid gap-3">
            <legend className="font-semibold">Your personal capabilities</legend>
            <label className="flex gap-2"><input type="checkbox" checked={leadWork} onChange={e=>setLeadWork(e.target.checked)}/>I can lead a job on site</label>
            <label className="flex gap-2"><input type="checkbox" checked={helperWork} onChange={e=>setHelperWork(e.target.checked)}/>I can join another crew as a technician or helper</label>
            <label className="grid gap-1">Technician level (optional)<select className="avcp-input" value={level} onChange={e=>setLevel(e.target.value)}><option value="">Choose later</option>{[4,3,2,1].map(n=><option key={n} value={`level_${n}_technician`}>Level {n} Technician</option>)}</select></label>
            <p className="text-xs text-[var(--avcp-ink-soft)]">This is your self-declared level. Being a lead or Level 4 technician does not mean you provide a crew.</p>
          </fieldset>}
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Email</span>
            <input
              data-testid={TID.signupEmail}
              className="avcp-input" type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Name (optional)</span>
            <input className="avcp-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Password</span>
            <input
              data-testid={TID.signupPassword}
              className="avcp-input" type="password" required minLength={12}
              value={password} onChange={(e) => setPassword(e.target.value)} />
            <span className="text-xs text-[var(--avcp-ink-soft)]">At least 12 characters. Mix letters, numbers, symbols.</span>
          </label>
          {err && <div className="text-sm text-[var(--avcp-danger)]">{err}</div>}
          <button data-testid={TID.signupSubmit} className="avcp-btn primary" disabled={busy || (role === "independent" && !leadWork && !helperWork)}>
            {busy ? "Creating…" : "Create account"}
          </button>
        </form>
        <p className="mt-6 text-sm text-[var(--avcp-ink-soft)]">
          Already have an account? <Link className="avcp-link" to="/signin">Sign in</Link>
        </p>
      </div>
    </Layout>
  );
}
